import { IAppliedRangeFilter } from '../../indigofair/data/IAppliedRangeFilter';
import { ISearchRequestReferrer } from '../../indigofair/data/ISearchRequestReferrer';
import { ISeasonYear } from '../../indigofair/data/ISeasonYear';

export namespace ISearchMakerProductsRequest {
  export namespace ISortingHint {
    /**
     * Return an instance of ISortingHint with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (partial?: Partial<ISortingHint>): ISortingHint =>
      ({
        ordered_categories: [],
        filter_keys: [],
        ordered_product_tokens: [],
        ...(partial ?? {}),
      }) as ISortingHint;
  }

  export interface ISortingHint {
    ordered_categories: Array<string>;
    boost_products_query: string | undefined;
    filter_keys: Array<string>;
    /**
     * If provided, these products will be boosted to the top. This currently works only for the FEATURED sort.
     */
    ordered_product_tokens: Array<string>;
  }

  export namespace IRankingBoost {
    /**
     * Return an instance of IRankingBoost with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (partial?: Partial<IRankingBoost>): IRankingBoost =>
      ({
        referrer_filters: [],
        referrer_products: [],
        ...(partial ?? {}),
      }) as IRankingBoost;
  }

  export interface IRankingBoost {
    should_use_sorting_hint: boolean | undefined;
    sort_by: keyof typeof ISearchMakerProductsRequest.SortBy | undefined;
    selected_category: string | undefined;
    referrer_category: string | undefined;
    /**
     * Use referrer category to provide the category's navigation path
     * @deprecated
     */
    referrer_sub_category: string | undefined;
    referrer_search: string | undefined;
    referrer_filters: Array<string>;
    referrer_products: Array<string>;
  }

  /**
   * The tags are out of order. All tag numbers must be unique. When adding fields, also update the field below and the tag number.
   * See https:www.notion.so/faire/Protocol-Buffers-Protos-ba527d2f70c34ab983b8d0417c6a56ac#6a792ceb9d37498795a117e712b9e7e7 for details.
   * The last tag added was `returnRetrievalAndRankingDebugInfo`. The next tag number is 39.
   */
  export enum SortBy {
    /**
     * Last modified first.
     */
    LAST_MODIFIED = 'LAST_MODIFIED',
    /**
     * Sort alphabetically.
     */
    A_TO_Z = 'A_TO_Z',
    Z_TO_A = 'Z_TO_A',
    FEATURED = 'FEATURED',
    /**
     * Active products first (with FEATURED as tie-breaker).
     */
    ACTIVE_FIRST = 'ACTIVE_FIRST',
    /**
     * Discontinued + Backordered products first (with FEATURED as tie-breaker).
     */
    INACTIVE_FIRST = 'INACTIVE_FIRST',
    /**
     * Sort by Wholesale price.
     */
    WHOLESALE_PRICE_DESC = 'WHOLESALE_PRICE_DESC',
    WHOLESALE_PRICE_ASC = 'WHOLESALE_PRICE_ASC',
    NEWEST = 'NEWEST',
    /**
     * Sort by the category order specified in SortingHint.
     * Products in unspecified categories will be put to the end of the list.
     */
    CATEGORIES = 'CATEGORIES',
    /**
     * Boost the products that match the Elastic Search query in SortingHint.
     */
    BOOST_PRODUCTS_QUERY = 'BOOST_PRODUCTS_QUERY',
    /**
     * Boost new products (usually when brand is known)
     */
    BOOST_NEW_PRODUCTS_QUERY = 'BOOST_NEW_PRODUCTS_QUERY',
    /**
     * Sort by products that match the most filter keys first. Ties are broken by FEATURED ranking.
     * Intended to boost maker products ranking based of filters used in category navigation.
     */
    FILTER_KEYS = 'FILTER_KEYS',
    /**
     * Sort by Retail price.
     */
    RETAIL_PRICE_DESC = 'RETAIL_PRICE_DESC',
    RETAIL_PRICE_ASC = 'RETAIL_PRICE_ASC',
    /**
     * Sort by most ordered products.
     */
    MOST_ORDERED_DESC = 'MOST_ORDERED_DESC',
    /**
     * Sort by most recently ordered products.
     */
    MOST_RECENT_ORDERED_DESC = 'MOST_RECENT_ORDERED_DESC',
    /**
     * Sort by the brand's best selling products.
     * @deprecated
     */
    BESTSELLERS = 'BESTSELLERS',
    /**
     * Use brand sourced ordering.
     */
    BRAND_ARRANGEMENT = 'BRAND_ARRANGEMENT',
  }

  /**
   * Expected for use by web - we can use requestInformationProvider for Mobile App.
   */
  export enum ClientSize {
    CLIENT_SIZE_UNKNOWN = 'CLIENT_SIZE_UNKNOWN',
    DESKTOP = 'DESKTOP',
    MOBILE = 'MOBILE',
  }

  /**
   * Return an instance of ISearchMakerProductsRequest with the provided fields. Any field not
   * listed will be populated with an empty value (an empty array,
   * or undefined, whichever is valid for the type of the value.)
   */
  export const build = (
    partial?: Partial<ISearchMakerProductsRequest>
  ): ISearchMakerProductsRequest =>
    ({
      filter_keys: [],
      range_filters: [],
      ...(partial ?? {}),
    }) as ISearchMakerProductsRequest;
}

export interface ISearchMakerProductsRequest {
  brand_token: string | undefined;
  category: string | undefined;
  query: string | undefined;
  /**
   * If set, specifies an explicit sorting. If not set, last modified is the default.
   * sortBy is only used for ranking boost, so it should be passed by the clients using
   * the [rankingBoost] message.
   * @deprecated
   */
  sort_by: keyof typeof ISearchMakerProductsRequest.SortBy | undefined;
  include_inactive: boolean | undefined;
  page_number: number | undefined;
  page_size: number | undefined;
  /**
   * The index of the first product to return (0-indexed), used with pageIndexCount.
   * Should not be set if pageNumber or pageSize are not null.
   * For example, a pageStartIndex of 10 would start the results on the 11th product, as it's zero-indexed, and return
   * pageIndexCount products.
   */
  page_start_index: number | undefined;
  /**
   * The max number of results to return, used with pageStartIndex.
   * Should not be set if pageNumber or pageSize are not null.
   * For example, a pageIndexCount of 10 would return 10 products, starting at pageStartIndex.
   */
  page_index_count: number | undefined;
  /**
   * @deprecated
   */
  include_restockable_quantities: boolean | undefined;
  /**
   * If true, backordered products and product options will be included in the response.
   * include_sales_paused should be used instead
   * @deprecated
   */
  include_backordered: boolean | undefined;
  /**
   * If true, SALES_PAUSED products and product options will be included in the response.
   */
  include_sales_paused: boolean | undefined;
  /**
   * sortingHint is calculated in the backend for this request, so no need for the clients to use it
   * @deprecated
   */
  sorting_hint: ISearchMakerProductsRequest.ISortingHint | undefined;
  /**
   * Whether to include prepacks as a separated list in the response.
   */
  include_pre_packs: boolean | undefined;
  /**
   * Whether to merge prepacks as fake product options on the response.
   */
  merge_pre_packs_into_options: boolean | undefined;
  /**
   * Whether to include options marked as "not for individual resale" as options on the response.
   */
  include_not_for_individual_sale_options: boolean | undefined;
  /**
   * Canonical ID (eg. America/Toronto). See here: https:www.joda.org/joda-time/timezones.html
   * @deprecated
   */
  timezone: string | undefined;
  /**
   * Whether to include sample products as options.
   * @deprecated
   */
  include_samples: boolean | undefined;
  /**
   * For brand page seasonal nav sections only, to request products in this SeasonYear.
   * @deprecated
   */
  season_year: ISeasonYear | undefined;
  /**
   * For brand page seasonal nav sections only, to request products in the earliest SeasonYear with products.
   * If true, seasonYear will be ignored.
   * @deprecated
   */
  return_earliest_season_year_with_products: boolean | undefined;
  filter_keys: Array<string>;
  /**
   * Deprecated without replacement. Feature is being dropped
   * @deprecated
   */
  range_filters: Array<IAppliedRangeFilter>;
  return_filter_sections: boolean | undefined;
  simplified_result_counts: boolean | undefined;
  client_size: keyof typeof ISearchMakerProductsRequest.ClientSize | undefined;
  /**
   * If true, the request is from buy it again page.
   * This feature is moved to GetBuyItAgainProductsRequest.
   * @deprecated
   */
  buy_it_again: boolean | undefined;
  /**
   * If true, the request is from brand page buy it again carousel.
   * This feature is moved to GetBuyItAgainProductsRequest.
   * @deprecated
   */
  buy_it_again_carousel: boolean | undefined;
  /**
   * The component that referred the user to this search.
   * This is primarily tracked for experiment metrics and data science purposes
   */
  referrer: ISearchRequestReferrer | undefined;
  ranking_boost: ISearchMakerProductsRequest.IRankingBoost | undefined;
  return_retrieval_and_ranking_debug_info: boolean | undefined;
}
